"use client"

import { richTextToPlain } from "../../common/rich-text/richText"

import { useEffect, useRef, useMemo, useCallback, useState } from "react"
import Message from "./Message"
import useGetMessages from "../../hooks/useGetMessages"
import { useDispatch, useSelector } from "react-redux"
import { markMessagesAsRead, resetLastAction } from "../../Slices/ChatSlice"

const SCROLL_BOTTOM_THRESHOLD = 120
const INITIAL_SCROLL_RETRIES = 6
const INITIAL_SCROLL_RETRY_DELAY = 60
const SCROLL_TOP_THRESHOLD = 100

// Utility function to format the date
const formatDate = (date) => {
  const today = new Date().setHours(0, 0, 0, 0)
  const messageDate = new Date(date).setHours(0, 0, 0, 0)
  const yesterday = today - 86400000

  if (messageDate === today) return "Today"
  if (messageDate === yesterday) return "Yesterday"

  const options = { year: "numeric", month: "long", day: "numeric" }
  return new Date(date).toLocaleDateString(undefined, options)
}

const Messages = ({ setReplyingTo, setEditingMessage, editingMessage }) => {
  const [pageNumber, setPageNumber] = useState(1)
  const [hasInitialPositioned, setHasInitialPositioned] = useState(false)
  // Number of messages that arrived while the user was scrolled up reading
  // history. Drives the "jump to latest" pill, which is the only signal that
  // something landed below the fold (auto-scroll deliberately does not fire
  // in that case, so new messages would otherwise arrive silently).
  const [newMessageCount, setNewMessageCount] = useState(0)
  // Short, polite announcement for screen readers. New messages, like the
  // typing indicator, are otherwise a purely visual change.
  const [liveAnnouncement, setLiveAnnouncement] = useState("")
  const dispatch = useDispatch()
  const lastAction = useSelector((state) => state.conversation.lastAction)
  const messages = useSelector((state) => state?.conversation?.messages || [])
  const messagesContainerRef = useRef(null)
  const selectedConversationData = useSelector((state) => state.conversation)

  const isLoadingMoreRef = useRef(false)
  const prevLastMessageIdRef = useRef(null)
  const prevMessagesLengthRef = useRef(0)
  const paginationScrollRef = useRef({ prevScrollHeight: 0, prevScrollTop: 0 })
  const initialScrollRetryTimeoutRef = useRef(null)
  const isFirstLoadRef = useRef(true)
  const currentConversationIdRef = useRef(null)

  const selectedConversationType = selectedConversationData?.conversationType
  const receiverId = selectedConversationData?.selectedConversation?.value
  const currentUserId = useSelector((state) => state?.data?.userInfo?.data?.data?.UserId)
  const connection = useSelector((state) => state.socket.socketConnection)

  const { loading, totalPages } = useGetMessages(pageNumber, receiverId, selectedConversationType)

  // Reset state when conversation changes - IMPROVED
  useEffect(() => {
    const newConversationId = `${receiverId}-${selectedConversationType}`
    
    // Only reset if the conversation actually changed
    if (currentConversationIdRef.current !== newConversationId) {
      currentConversationIdRef.current = newConversationId
      
      setPageNumber(1)
      setHasInitialPositioned(false)
      isLoadingMoreRef.current = false
      isFirstLoadRef.current = true
      
      if (initialScrollRetryTimeoutRef.current) {
        clearTimeout(initialScrollRetryTimeoutRef.current)
        initialScrollRetryTimeoutRef.current = null
      }
      
      prevLastMessageIdRef.current = null
      prevMessagesLengthRef.current = 0
      paginationScrollRef.current = { prevScrollHeight: 0, prevScrollTop: 0 }
      setNewMessageCount(0)
      setLiveAnnouncement("")
    }
  }, [receiverId, selectedConversationType])

  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return true

    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      SCROLL_BOTTOM_THRESHOLD
    )
  }, [])

  const scrollToBottom = useCallback((behavior = "auto") => {
    const container = messagesContainerRef.current
    if (!container) return

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    })
  }, [])

  const scrollToBottomWithRetry = useCallback(
    (attempt = 0) => {
      scrollToBottom("auto")

      const container = messagesContainerRef.current
      if (!container) return

      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight

      if (distanceFromBottom <= 2 || attempt >= INITIAL_SCROLL_RETRIES) {
        return
      }

      initialScrollRetryTimeoutRef.current = setTimeout(() => {
        scrollToBottomWithRetry(attempt + 1)
      }, INITIAL_SCROLL_RETRY_DELAY)
    },
    [scrollToBottom],
  )

  const isUnreadForCurrentUser = useCallback(
    (message) => {
      if (selectedConversationType === "group") {
        const seenUsers = message?.seenUsers || []
        return !seenUsers.some((seen) => Number(seen?.userId) === Number(currentUserId))
      }

      return !message?.isRead && Number(message?.senderId) !== Number(currentUserId)
    },
    [currentUserId, selectedConversationType],
  )

  const scrollToFirstUnreadOrBottom = useCallback(() => {
    const firstUnreadMessage = messages.find((message) => isUnreadForCurrentUser(message))

    if (firstUnreadMessage) {
      const unreadElement = document.getElementById(`message-${firstUnreadMessage.id}`)
      if (unreadElement) {
        unreadElement.scrollIntoView({ behavior: "auto", block: "start" })
        return true
      }
    }
    
    scrollToBottomWithRetry(0)
    return false
  }, [messages, isUnreadForCurrentUser, scrollToBottomWithRetry])

  // IMPROVED: Initial positioning with proper loading handling
  useEffect(() => {
    // Don't position if:
    // 1. Still loading first page
    // 2. No messages
    // 3. Already positioned
    // 4. Not on first page
    if (loading || messages.length === 0 || hasInitialPositioned || pageNumber !== 1) {
      return
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      // Double-check conditions haven't changed
      if (!hasInitialPositioned && messages.length > 0 && messagesContainerRef.current) {
        scrollToFirstUnreadOrBottom()
        setHasInitialPositioned(true)
        isFirstLoadRef.current = false
        prevMessagesLengthRef.current = messages.length
        prevLastMessageIdRef.current = messages[messages.length - 1]?.id || null
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [loading, messages.length, hasInitialPositioned, pageNumber, scrollToFirstUnreadOrBottom])

  // IMPROVED: Handle new messages with better scroll behavior
  useEffect(() => {
    if (messages.length === 0 || loading) return

    const lastMessage = messages[messages.length - 1]
    const lastMessageId = lastMessage?.id

    // Check for new message (appended, not loaded via pagination)
    const hasNewMessage = 
      !isLoadingMoreRef.current &&
      hasInitialPositioned &&
      messages.length > prevMessagesLengthRef.current &&
      lastMessageId !== prevLastMessageIdRef.current

    if (hasNewMessage) {
      const sentByCurrentUser = Number(lastMessage?.senderId) === Number(currentUserId)
      const shouldScrollToBottom = sentByCurrentUser || isNearBottom()

      if (shouldScrollToBottom) {
        scrollToBottom("smooth")
        // Following along at the bottom — nothing is hidden below the fold.
        setNewMessageCount(0)
      }

      if (!sentByCurrentUser) {
        const preview =
          richTextToPlain(lastMessage?.content || "") || lastMessage?.fileName || "attachment"
        setLiveAnnouncement(`New message: ${preview}`)

        // Arrived off-screen while reading back — surface it in the pill.
        if (!shouldScrollToBottom) {
          setNewMessageCount((count) => count + 1)
        }
      }
    }

    // Update refs
    prevMessagesLengthRef.current = messages.length
    prevLastMessageIdRef.current = lastMessageId || null

    // Reset lastAction if present
    if (lastAction) {
      dispatch(resetLastAction())
    }
  }, [messages, loading, lastAction, currentUserId, isNearBottom, scrollToBottom, dispatch, hasInitialPositioned])

  // IMPROVED: Restore scroll position after loading older messages
  useEffect(() => {
    if (!messagesContainerRef.current) {
      isLoadingMoreRef.current = false
      return
    }

    if (!isLoadingMoreRef.current || loading) return

    const container = messagesContainerRef.current
    const { prevScrollHeight, prevScrollTop } = paginationScrollRef.current
    
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (container) {
        const newScrollTop = container.scrollHeight - prevScrollHeight + prevScrollTop
        container.scrollTop = Math.max(0, newScrollTop)
      }
      isLoadingMoreRef.current = false
    })
  }, [loading, messages])

  // Handle scroll to top for pagination
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const { scrollTop } = container
    
    if (
      scrollTop <= SCROLL_TOP_THRESHOLD &&
      !loading &&
      pageNumber < totalPages &&
      !isLoadingMoreRef.current &&
      hasInitialPositioned // Only allow pagination after initial positioning
    ) {
      paginationScrollRef.current = {
        prevScrollHeight: container.scrollHeight,
        prevScrollTop: scrollTop,
      }
      isLoadingMoreRef.current = true
      setPageNumber((prevPage) => prevPage + 1)
    }

    // Scrolling back down to the latest message dismisses the pill.
    if (isNearBottom()) {
      setNewMessageCount((count) => (count === 0 ? count : 0))
    }
  }, [loading, pageNumber, totalPages, hasInitialPositioned, isNearBottom])

  const handleJumpToLatest = useCallback(() => {
    scrollToBottom("smooth")
    setNewMessageCount(0)
  }, [scrollToBottom])

  const handleMessagesRead = useCallback((data) => {
    const { readerId, targetUserId } = data

    if (
      (receiverId == readerId && currentUserId == targetUserId) ||
      (targetUserId == receiverId && readerId != currentUserId)
    ) {
      dispatch(
        markMessagesAsRead({
          readerId,
          targetUserId,
          conversationType: selectedConversationType,
        }),
      )
    }
  }, [receiverId, currentUserId, dispatch, selectedConversationType])

  useEffect(() => {
    if (!connection) return

    connection.on("MessagesRead", handleMessagesRead)
    return () => {
      connection.off("MessagesRead", handleMessagesRead)
    }
  }, [connection, handleMessagesRead])

  // Group messages by day - memoized
  const groupedMessages = useMemo(() => {
    return messages.reduce((acc, message) => {
      const messageDate = new Date(message.createdAt).setHours(0, 0, 0, 0)
      if (!acc[messageDate]) {
        acc[messageDate] = []
      }
      acc[messageDate].push(message)
      return acc
    }, {})
  }, [messages])

  const renderMessageGroups = useCallback(() => {
    const groups = Object.keys(groupedMessages).map((dateKey) => {
      const groupMessages = groupedMessages[dateKey]
      const formattedDate = formatDate(Number.parseInt(dateKey))
      return (
        <div key={dateKey}>
          <div className="flex items-center justify-center my-4">
            {!loading && (
              <>
                <div className="flex-grow border-t border-gray-300 ml-56"></div>
                <span className="mx-4 text-xs text-gray-500">{formattedDate}</span>
                <div className="flex-grow border-t border-gray-300 mr-56"></div>
              </>
            )}
          </div>

          {groupMessages.map((message) => (
            <div key={message.id} id={`message-${message.id}`}>
              <Message
                message={message}
                loading={loading}
                setReplyingTo={setReplyingTo}
                setEditingMessage={setEditingMessage}
                editingMessage={editingMessage}
              />
            </div>
          ))}
        </div>
      )
    })
    return groups
  }, [groupedMessages, loading, setReplyingTo, setEditingMessage, editingMessage])

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        className="px-4 flex-1 overflow-auto"
        onScroll={handleScroll}
        ref={messagesContainerRef}
      >
        {pageNumber === totalPages && pageNumber > 1 && (
          <p className="text-center text-gray-500 my-2">No more messages</p>
        )}
        {messages.length > 0 ? (
          renderMessageGroups()
        ) : !loading ? (
          <p className="text-center opacity-50 text-black">Send a message to start the conversation</p>
        ) : null}
      </div>

      {/* Jump to latest. Only rendered while messages are waiting below the
          fold, so it never covers the thread during normal reading. */}
      {newMessageCount > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <button
            type="button"
            onClick={handleJumpToLatest}
            className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-sideNavbg px-3 py-1.5 text-xs font-semibold text-white shadow-lg transition-colors hover:bg-buttonBg focus:outline-none focus-visible:ring-2 focus-visible:ring-brandAccent"
          >
            {newMessageCount} new message{newMessageCount > 1 ? "s" : ""}
            <span aria-hidden="true">&darr;</span>
          </button>
        </div>
      )}

      {/* Announces incoming messages to screen readers; renders nothing. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
    </div>
  )
}

export default Messages
